/**
 * OracleHistory — searchable notification history page.
 * Mounted at /admin/security/oracle. Owner-only per RBAC on the endpoint,
 * but the endpoint returns admin-visible masked data for non-owner admins
 * too, so this page renders for anyone reaching it.
 *
 * Filters: severity, category, resolved, since (Today/7d/30d/90d/All), search.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Loader2, ExternalLink } from "lucide-react";
import { oracleEvents, oracleResolve, oracleResolveBulk } from "../../lib/api";

const SEV = ["critical", "warning", "info", "team"];
const CATS = ["security", "members", "subscribers", "editorial", "email",
              "dispatch", "jericho", "recovery", "analytics", "deployments", "system"];
const RANGES = [
  { key: "today", label: "Today",  days: 1 },
  { key: "7d",    label: "7 Days", days: 7 },
  { key: "30d",   label: "30 Days", days: 30 },
  { key: "90d",   label: "90 Days", days: 90 },
  { key: "all",   label: "All",     days: null },
];
const SEV_COLOR = { critical: "#D92D20", warning: "#E0A526", info: "#3B82F6", team: "#3FB950" };

export const OracleHistory = ({ opToken, userToken }) => {
  const [severity, setSeverity] = useState("");
  const [category, setCategory] = useState("");
  const [range, setRange] = useState("7d");
  const [resolved, setResolved] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const sinceIso = useMemo(() => {
    const opt = RANGES.find((r) => r.key === range);
    if (!opt?.days) return null;
    const d = new Date(Date.now() - opt.days * 86400000);
    return d.toISOString();
  }, [range]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (severity) params.severity = severity;
      if (category) params.category = category;
      if (sinceIso) params.since = sinceIso;
      if (resolved !== "") params.resolved = resolved === "1";
      if (q.trim()) params.q = q.trim();
      const r = await oracleEvents(opToken || null, userToken || null, params);
      setItems(r.items || []);
    } catch {
      toast.error("History load failed");
    } finally { setLoading(false); }
  }, [severity, category, sinceIso, resolved, q, opToken, userToken]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const resolveSelected = async () => {
    if (!selected.size) return;
    try {
      const r = await oracleResolve(opToken || null, userToken || null, Array.from(selected));
      toast.success(`Resolved ${r.resolved} events`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resolve failed");
    }
  };

  const bulkReview = async (severities, label) => {
    if (!window.confirm(`${label}? Events are marked reviewed — they stay in history, nothing is deleted.`)) return;
    try {
      const r = await oracleResolveBulk(opToken || null, userToken || null, severities);
      toast.success(`Marked ${r.resolved} events reviewed`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk review failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="oracle-history-page">
      <div className="flex items-center gap-2 text-[#D92D20]">
        <span className="font-mono text-[11px] uppercase tracking-[0.3em]">Oracle · Notification History</span>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-1 border border-[#2D2A26] px-2 py-1.5 bg-[#0d0c0a]">
          <Search size={12} className="text-[#A39E93]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title / message / source…"
            data-testid="oracle-history-search"
            className="bg-transparent text-[12px] text-[#E4DFD3] outline-none font-mono min-w-[200px]"
          />
        </div>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                data-testid="oracle-history-severity"
                className="bg-[#0d0c0a] border border-[#2D2A26] text-[11px] text-[#A39E93] font-mono px-2 py-1.5">
          <option value="">Any severity</option>
          {SEV.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="oracle-history-category"
                className="bg-[#0d0c0a] border border-[#2D2A26] text-[11px] text-[#A39E93] font-mono px-2 py-1.5">
          <option value="">Any category</option>
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex border border-[#2D2A26]" data-testid="oracle-history-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={
                "px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors " +
                (range === r.key ? "bg-[#1a0808] text-[#D92D20]" : "text-[#A39E93] hover:text-[#E4DFD3]")
              }
            >{r.label}</button>
          ))}
        </div>
        <select value={resolved} onChange={(e) => setResolved(e.target.value)}
                data-testid="oracle-history-resolved"
                className="bg-[#0d0c0a] border border-[#2D2A26] text-[11px] text-[#A39E93] font-mono px-2 py-1.5">
          <option value="">Any status</option>
          <option value="0">Unresolved</option>
          <option value="1">Resolved</option>
        </select>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={resolveSelected}
            data-testid="oracle-history-resolve"
            className="ml-auto px-3 py-1.5 border border-[#3FB950] text-[#3FB950] font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#0d1a10]"
          >Resolve {selected.size} selected</button>
        )}
        <button
          type="button"
          onClick={() => bulkReview(["info"], "Mark ALL routine info events as reviewed")}
          data-testid="oracle-history-review-info"
          className={(selected.size > 0 ? "" : "ml-auto ") + "px-3 py-1.5 border border-[#3B82F6]/50 text-[#3B82F6] font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#0a1220]"}
        >Mark routine info reviewed</button>
        <button
          type="button"
          onClick={() => bulkReview([], "Acknowledge ALL current unresolved events")}
          data-testid="oracle-history-ack-all"
          className="px-3 py-1.5 border border-[#454038] text-[#A39E93] font-mono text-[10px] uppercase tracking-[0.25em] hover:border-[#A39E93] hover:text-[#E4DFD3]"
        >Acknowledge all current</button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#A39E93] font-mono text-[12px]">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-[#2D2A26] bg-[#0d0c0a] p-6 text-[12px] font-mono text-[#757065]">
          No events match the current filters.
        </div>
      ) : (
        <ul className="border border-[#2D2A26] divide-y divide-[#1f1c18]" data-testid="oracle-history-list">
          {items.map((ev) => (
            <li key={ev.id} className="flex items-start gap-3 px-3 py-2 hover:bg-[#131211]">
              <input
                type="checkbox"
                checked={selected.has(ev.id)}
                onChange={() => toggle(ev.id)}
                aria-label={`Select ${ev.title}`}
                className="mt-1"
              />
              <div className="w-1 self-stretch" style={{ background: SEV_COLOR[ev.severity] || "#2D2A26" }} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[#E4DFD3] font-medium">{ev.title}</div>
                {ev.message && <div className="text-[11px] text-[#A39E93]">{ev.message}</div>}
                <div className="flex flex-wrap items-center gap-2 mt-1 text-[9px] font-mono uppercase tracking-[0.2em] text-[#757065]">
                  <span style={{ color: SEV_COLOR[ev.severity] }}>{ev.severity}</span>
                  <span>·</span>
                  <span>{ev.category}</span>
                  <span>·</span>
                  <span>{new Date(ev.ts).toLocaleString()}</span>
                  {ev.actor_email && <><span>·</span><span>{ev.actor_email}</span></>}
                  {ev.resolved && <><span>·</span><span className="text-[#3FB950]">RESOLVED</span></>}
                </div>
              </div>
              {ev.href && (
                <Link
                  to={ev.href}
                  className="text-[#A39E93] hover:text-[#D92D20] mt-1"
                  aria-label="Jump to resolving page"
                >
                  <ExternalLink size={12} />
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default OracleHistory;
